'use client'

import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Minus,
  Plus,
  Trash2,
  ShoppingCart,
  ArrowLeft,
  Maximize2,
  Check,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { useCartStore, type CartItem } from '@/store/use-cart-store'
import { useNavigationStore } from '@/store/use-navigation-store'
import { useCartAnimationStore } from '@/store/use-cart-animation-store'

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatPrice(price: number): string {
  return price.toLocaleString('ru-RU') + ' ₽'
}

const FREE_DELIVERY_THRESHOLD = 15000
const DELIVERY_COST = 500

function getItemsWord(count: number): string {
  const abs = Math.abs(count) % 100
  const n1 = abs % 10
  if (abs > 10 && abs < 20) return 'товаров'
  if (n1 > 1 && n1 < 5) return 'товара'
  if (n1 === 1) return 'товар'
  return 'товаров'
}

/* ------------------------------------------------------------------ */
/*  Animated Quantity Display                                          */
/* ------------------------------------------------------------------ */

function AnimatedQuantity({ quantity }: { quantity: number }) {
  return (
    <motion.span
      key={quantity}
      initial={{ scale: 1.4, color: 'oklch(0.35 0.12 30)' }}
      animate={{ scale: 1, color: 'inherit' }}
      transition={{ duration: 0.3 }}
      className="w-7 text-center text-sm font-medium tabular-nums"
    >
      {quantity}
    </motion.span>
  )
}

/* ------------------------------------------------------------------ */
/*  Cart Drawer Item Row                                               */
/* ------------------------------------------------------------------ */

function CartDrawerItemRow({
  item,
  onUpdateQuantity,
  onRemove,
}: {
  item: CartItem
  onUpdateQuantity: (qty: number) => void
  onRemove: () => void
}) {
  const subtotal = item.price * item.quantity

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.25 }}
      className="flex gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors duration-200"
    >
      {/* Product image */}
      <div className="h-20 w-16 shrink-0 overflow-hidden rounded-md bg-muted">
        <img
          src={item.productImage || '/placeholder-door.jpg'}
          alt={item.productName}
          className="h-full w-full object-cover"
        />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-sm truncate leading-tight">
          {item.productName}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {item.width} мм
          {item.colorName && <span className="ml-1">{item.colorName}</span>}
        </p>
        <p className="text-sm font-medium mt-1">{formatPrice(item.price)}</p>
      </div>

      {/* Controls */}
      <div className="flex flex-col items-end justify-between shrink-0">
        {/* Remove */}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive tactile-press"
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>

        {/* Quantity with animation */}
        <div className="flex items-center gap-0.5">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 tactile-press"
            onClick={() => onUpdateQuantity(item.quantity - 1)}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <AnimatedQuantity quantity={item.quantity} />
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 tactile-press"
            onClick={() => onUpdateQuantity(item.quantity + 1)}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>

        {/* Subtotal */}
        <span className="text-sm font-bold">{formatPrice(subtotal)}</span>
      </div>
    </motion.div>
  )
}

/* ------------------------------------------------------------------ */
/*  Bouncing Shopping Bag SVG                                           */
/* ------------------------------------------------------------------ */

function BouncingBag() {
  return (
    <div className="bounce-bag">
      <div className="mb-4 rounded-full bg-muted p-5 inline-block">
        <ShoppingCart className="h-10 w-10 text-muted-foreground" />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  "Товар добавлен" success banner                                   */
/* ------------------------------------------------------------------ */

function AddedSuccessBanner({ onOpen }: { onOpen: () => void }) {
  const [visible, setVisible] = useState(true)
  if (!visible) return null
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="mx-4 mb-3 flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800/40 px-3 py-2"
    >
      <Check className="h-4 w-4 text-green-600 shrink-0" />
      <span className="text-sm text-green-700 dark:text-green-400 font-medium flex-1">
        Товар добавлен в корзину
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30"
        onClick={() => {
          setVisible(false)
          onOpen()
        }}
      >
        Оформить
      </Button>
    </motion.div>
  )
}

/* ------------------------------------------------------------------ */
/*  CartDrawer Component                                               */
/* ------------------------------------------------------------------ */

export function CartDrawer() {
  const cartDrawerOpen = useNavigationStore((s) => s.cartDrawerOpen)
  const setCartDrawerOpen = useNavigationStore((s) => s.setCartDrawerOpen)
  const navigate = useNavigationStore((s) => s.navigate)

  const items = useCartStore((s) => s.items)
  const updateQuantity = useCartStore((s) => s.updateQuantity)
  const removeItem = useCartStore((s) => s.removeItem)
  const totalItems = useCartStore((s) => s.totalItems)
  const promoCode = useCartStore((s) => s.promoCode)
  const promoDiscount = useCartStore((s) => s.promoDiscount)

  const bounceKey = useCartAnimationStore((s) => s.cartBounceKey)

  const prevItemCount = useRef(totalItems())

  // Auto-open drawer on first add (when items go from 0 to 1)
  useEffect(() => {
    const currentCount = totalItems()
    if (currentCount > 0 && prevItemCount.current === 0) {
      setCartDrawerOpen(true)
    }
    prevItemCount.current = currentCount
  }, [bounceKey, totalItems, setCartDrawerOpen])

  const rawSubtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  )
  const subtotalAfterDiscount = Math.max(0, rawSubtotal - promoDiscount)
  const deliveryCost =
    rawSubtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_COST
  const total = subtotalAfterDiscount + deliveryCost

  const handleUpdateQuantity = (
    productId: string,
    skuId: string,
    colorId: string | null,
    qty: number
  ) => {
    updateQuantity(productId, skuId, colorId, qty)
  }

  const handleRemove = (
    productId: string,
    skuId: string,
    colorId: string | null
  ) => {
    removeItem(productId, skuId, colorId)
  }

  const handleOpenFullPage = () => {
    setCartDrawerOpen(false)
    setTimeout(() => {
      navigate('cart-full')
    }, 300)
  }

  const handleGoToCheckout = () => {
    setCartDrawerOpen(false)
    setTimeout(() => {
      navigate('checkout')
    }, 300)
  }

  return (
    <Sheet open={cartDrawerOpen} onOpenChange={(open) => {
      if (!open) {
        setCartDrawerOpen(false)
      }
    }}>
      <SheetContent
        side="right"
        className="w-full max-w-md flex flex-col p-0"
      >
        {/* Header */}
        <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0">
          <div className="flex items-center justify-between pr-6">
            <div className="flex items-center gap-3">
              <SheetTitle className="text-xl font-bold">
                Корзина
              </SheetTitle>
              {items.length > 0 && (
                <span className="text-sm font-normal text-muted-foreground">
                  {totalItems()} {getItemsWord(totalItems())}
                </span>
              )}
            </div>
            {items.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={handleOpenFullPage}
              >
                <Maximize2 className="h-3.5 w-3.5" />
                Полная страница
              </Button>
            )}
          </div>
          <SheetDescription className="sr-only">
            Корзина покупок
          </SheetDescription>
        </SheetHeader>

        {/* Success banner when just opened from auto-add */}
        {items.length > 0 && cartDrawerOpen && bounceKey > 0 && (
          <AddedSuccessBanner onOpen={handleGoToCheckout} />
        )}

        {/* Empty state with bouncing bag */}
        {items.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center px-4 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <BouncingBag />
              <h3 className="text-lg font-bold mb-1">Корзина пуста</h3>
              <p className="text-sm text-muted-foreground mb-5">
                Добавьте товары из каталога
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setCartDrawerOpen(false)
                  setTimeout(() => navigate('catalog'), 300)
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                В каталог
              </Button>
            </motion.div>
          </div>
        )}

        {/* Cart items list */}
        {items.length > 0 && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              <AnimatePresence mode="popLayout">
                {items.map((item) => (
                  <CartDrawerItemRow
                    key={`${item.productId}-${item.skuId}-${item.colorId}`}
                    item={item}
                    onUpdateQuantity={(qty) =>
                      handleUpdateQuantity(
                        item.productId,
                        item.skuId,
                        item.colorId,
                        qty
                      )
                    }
                    onRemove={() =>
                      handleRemove(item.productId, item.skuId, item.colorId)
                    }
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* Sticky bottom total + checkout with gradient strip */}
            <div className="shrink-0 border-t gradient-strip px-4 py-4 space-y-3">
              {promoDiscount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">
                    Скидка{promoCode ? ` (${promoCode})` : ''}
                  </span>
                  <span className="font-medium text-green-600">
                    -{formatPrice(promoDiscount)}
                  </span>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Доставка</span>
                {deliveryCost === 0 ? (
                  <span className="font-medium text-green-600">Бесплатно</span>
                ) : (
                  <span className="font-medium">{formatPrice(deliveryCost)}</span>
                )}
              </div>

              {deliveryCost > 0 && (
                <p className="text-xs text-muted-foreground">
                  Бесплатно от {formatPrice(FREE_DELIVERY_THRESHOLD)}
                </p>
              )}

              <Separator />

              <div className="flex justify-between">
                <span className="text-lg font-bold">Итого</span>
                <span className="text-lg font-bold text-primary">
                  {formatPrice(total)}
                </span>
              </div>

              <Button
                size="lg"
                className="w-full text-base btn-shine"
                onClick={handleGoToCheckout}
              >
                Оформить заказ
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setCartDrawerOpen(false)
                  setTimeout(() => navigate('catalog'), 300)
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Продолжить покупки
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
